/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk, createAction } from '@reduxjs/toolkit';
import { reverse, sortBy, prop } from 'ramda';
import { apiClient } from '../../../lib/apiClient';

export const getNewMexicoCaseInformation = createAsyncThunk(
  'caseInformationNewMexico/getNewMexicoCaseInformation',
  async (id) => {
    const { data } = await apiClient.get(`/api/newMexicoCases/${id}/caseInformation`);
    return data;
  },
);

export const resetNewMexicoCaseInformation = createAction(
  'caseInformationNewMexico/resetNewMexicoCaseInformation',
  () => {
    return {
      type: 'caseInformationNewMexico/resetNewMexicoCaseInformation',
    };
  },
);

export const getNewMexicoRelatedCases = createAsyncThunk(
  'caseInformationNewMexico/getNewMexicoRelatedCases',
  async ({ caseId }) => {
    const { data } = await apiClient.get(`/api/newMexicoCases/${caseId}/relativeCases`);
    return data;
  },
);

export const getNewMexicoLegalAssistants = createAsyncThunk(
  'caseInformationNewMexico/getNewMexicoLegalAssistants',
  async (id) => {
    const { data } = await apiClient.get(`/api/newMexicoCases/${id}/legalAssistants`);
    return data.result.map(({ userId, firstName, lastName }) => {
      return {
        id: userId,
        label: `${firstName} ${lastName}`,
        value: userId,
      };
    });
  },
);

export const updateNewMexicoCaseNumber = createAsyncThunk(
  'caseInformationNewMexico/updateNewMexicoCaseNumber',
  async ({ id, ...rest }, { rejectWithValue }) => {
    try {
      const { data } = await apiClient.put(`/api/newMexicoCases/${id}/caseInformation`, {
        caseId: id,
        ...rest,
      });
      return data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.errorMessage);
    }
  },
);

export const getNewMexicoStatusesForCase = createAsyncThunk(
  'caseInformationNewMexico/getNewMexicoStatusesForCase',
  async (id) => {
    const { data } = await apiClient.get(`/api/newMexicoCases/${id}/statuses`);

    const caseStatusToRevert = data?.result?.find((item) => {
      const isSystem = item.updatedBy.trim() === 'system';
      const isArchived = item.status === 'Archived';
      return isArchived || !isSystem;
    });

    let statusObj = null;
    if (caseStatusToRevert) {
      statusObj = await apiClient.get(`/api/newMexicoCases/${id}/statuses/canRevert`);
    }

    const adoptedStatuses = data?.result.map((item) => {
      if (item.caseStatusId === caseStatusToRevert?.caseStatusId) {
        return {
          ...item,
          caseId: id,
          mayBeReverted: statusObj?.data?.result,
        };
      }
      return { ...item, item, mayBeReverted: false };
    });

    return adoptedStatuses;
  },
);

export const getNewMexicoProceduralHistoryEntries = createAsyncThunk(
  'caseInformationNewMexico/getNewMexicoProceduralHistoryEntries',
  async (id) => {
    const { data } = await apiClient.get(`/api/newMexicoCases/${id}/proceduralHistoryEntries`);
    return data;
  },
);

export const getNewMexicoAttorneys = createAsyncThunk(
  'caseInformationNewMexico/getNewMexicoAttorneys',
  async (id) => {
    const { data } = await apiClient.get(`/api/cases/${id}/attorneys`);
    return data.result.map(({ userId, firstName, lastName, attorneyRate }) => {
      return {
        id: userId,
        label: `${firstName} ${lastName}`,
        value: userId,
        attorneyRate,
      };
    });
  },
);

export const getNewMexicoWritDate = createAsyncThunk(
  'caseInformationNewMexico/getNewMexicoWritDate',
  async (params, { signal }) => {
    const { data } = await apiClient.post(`/api/newMexicoCases/precalculations/writDate`, params, {
      signal,
    });
    return data?.result || null;
  },
);

export const addNewMexicoProceduralHistoryEntry = createAsyncThunk(
  'caseInformationNewMexico/addNewMexicoProceduralHistoryEntry',
  async ({ id, ...rest }, { rejectWithValue }) => {
    try {
      const { data } = await apiClient.post(
        `/api/newMexicoCases/${id}/proceduralHistoryEntries`,
        rest,
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

export const updateNewMexicoProceduralHistoryEntry = createAsyncThunk(
  'caseInformationNewMexico/updateNewMexicoProceduralHistoryEntry',
  async ({ id, proceduralHistoryEntryId, body }, { rejectWithValue }) => {
    try {
      const { data } = await apiClient.put(
        `/api/newMexicoCases/${id}/proceduralHistoryEntries/${proceduralHistoryEntryId}`,
        body,
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

export const removeNewMexicoProceduralHistoryEntry = createAsyncThunk(
  'caseInformationNewMexico/removeNewMexicoProceduralHistoryEntry',
  async ({ id, proceduralEntryId }, { rejectWithValue }) => {
    try {
      const { data } = await apiClient.delete(
        `/api/newMexicoCases/${id}/proceduralHistoryEntries/${proceduralEntryId}`,
      );

      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

export const getNewMexicoStatuses = createAsyncThunk(
  'caseInformationNewMexico/getNewMexicoStatuses',
  async () => {
    const { data } = await apiClient.get(`/api/newMexicoCases/statuses?OnlyManual=true`);

    return data.result.map(({ value }, id) => ({
      id,
      value,
      label: value,
    }));
  },
);

export const addNewMexicoStatusToCase = createAsyncThunk(
  'caseInformationNewMexico/addNewMexicoStatusToCase',
  async ({ id, ...rest }, { rejectWithValue }) => {
    try {
      const { data } = await apiClient.post(`/api/newMexicoCases/${id}/statuses`, rest);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

const initialState = {
  proceduralHistoryEntries: [],
  newMexicoCaseInformation: {},
  newMexicoStatuses: [],
  relatedNewMexicoCases: null,
  legalAssistants: [],
  attorneys: [],
  statuses: [],
  errors: null,
};

export const caseInformationNewMexico = createSlice({
  name: 'caseInformationNewMexico',
  initialState,
  reducers: {},
  extraReducers: {
    [getNewMexicoRelatedCases.fulfilled]: (state, action) => {
      state.relatedNewMexicoCases = action.payload.result;
    },
    [updateNewMexicoCaseNumber.rejected]: (state, action) => {
      state.errors = action.payload;
    },
    [removeNewMexicoProceduralHistoryEntry.rejected]: (state, action) => {
      state.errors = action.payload;
    },
    [addNewMexicoProceduralHistoryEntry.rejected]: (state, action) => {
      state.errors = action.payload;
    },
    [updateNewMexicoProceduralHistoryEntry.rejected]: (state, action) => {
      state.errors = action.payload;
    },
    [addNewMexicoStatusToCase.rejected]: (state, action) => {
      state.errors = action.payload;
    },
    [getNewMexicoStatusesForCase.fulfilled]: (state, action) => {
      state.newMexicoStatuses = reverse(sortBy(prop('caseStatusId'), action.payload));
    },
    [getNewMexicoStatusesForCase.rejected]: (state, action) => {
      state.errors = action.payload;
    },
    [resetNewMexicoCaseInformation]: (state) => {
      state.newMexicoCaseInformation = {};
    },
    [getNewMexicoCaseInformation.fulfilled]: (state, action) => {
      state.caseCreationDate = action.payload.result.creationDate;
      state.newMexicoCaseInformation = action.payload.result;
    },
    [getNewMexicoCaseInformation.rejected]: (state, action) => {
      state.errors = action.payload;
    },
    [getNewMexicoStatuses.fulfilled]: (state, action) => {
      state.statuses = action.payload;
    },
    [getNewMexicoStatuses.rejected]: (state, action) => {
      state.errors = action.payload;
    },
    [getNewMexicoAttorneys.fulfilled]: (state, action) => {
      state.attorneys = action.payload;
    },
    [getNewMexicoLegalAssistants.fulfilled]: (state, action) => {
      state.legalAssistants = action.payload;
    },
    [getNewMexicoAttorneys.rejected]: (state, action) => {
      state.errors = action.payload;
    },
    [getNewMexicoProceduralHistoryEntries.fulfilled]: (state, action) => {
      state.proceduralHistoryEntries = action.payload.result;
    },
    [getNewMexicoProceduralHistoryEntries.rejected]: (state, action) => {
      state.errors = action.payload;
    },
  },
});

export default caseInformationNewMexico.reducer;
